import { render, nothing, noChange, html, svg, repeat, cache, choose, classMap, guard, ifDefined, join, keyed, live, map, range, ref, createRef, styleMap, until, when, asyncAppend, asyncReplace, templateContent, unsafeHTML, unsafeSVG } from './module.js';
import { eventify } from './eventify.js'
import { signal, batch, computed, effect } from '@preact/signals-core'
import * as utils from './lithtmlutils.js'

let version = '0.9.2';
let { isArray, isObject, defineProperty, defineReadOnlyProperty, tryParse, camelize } = utils;

// INFO webcomponent rendering etc.
//      https://open-wc.org/blog/some-things-to-know-when-developing-with-lit/
//      https://open-wc.org/blog/lit-element-deepdive-in-batching-updates/
//      https://open-wc.org/blog/doing-a-flip-with-lit-html-2-0/
//      https://open-wc.org/guides/knowledge/events/

// lit-html templates documentation
//      https://lit.dev/docs/templates/overview/

/** Support events */
let events = eventify(Object.create(null));
delete events.deEventify;
delete events.clearEvents;

let noop = () => {}

// @ts-check
const emptyAttrib = {};

/** @type {import('./lithtmltypes').CustomHTMLElementOptions} */
let defaultOptions = {
    useShadowRoot: true,
    renderFrequency: 'batched' // raf? immediate? settimeout?
}

/**
 * Base class for new Custom Elements
 */
class CustomHTMLElement extends HTMLElement {
    /**
     * @param {typeof import("./lithtmltypes").CustomHTMLElement} derivedClass derived class with potentially observedAttributes and/or observeProperties attached to it
     * @param {import("./lithtmltypes").CustomHTMLElementOptions} options optional CustomHTMLElementOptions
     */
    constructor(derivedClass, options = {}) {
        super();

        let self = this;

        self.options = Object.assign({}, defaultOptions, options);

        /**
         * The name of the custom element
         * @type {string}
         */
        self._tag = derivedClass.tag || 'missing-tag';

        /**
         * List of all observable properties
         * @type {string[]}
         */
        self._obsProps = derivedClass?.observedProperties || [];

        /**
         * List of all the observable attributes.
         * @type {string[]}
         */
        self._obsAttrs = derivedClass?.observedAttributes || [];

        // Make sure all observed attributes has a corresponding (observed) property
        self._attrToProps = Object.create(null);
        self._propToAttrs = Object.create(null);
        self._obsAttrs.forEach(attr => {
            let propName = camelize(attr);

            self._attrToProps[attr]     = propName;
            self._propToAttrs[propName] = attr;
            self._obsProps.indexOf(propName) === -1 && self._obsProps.push(propName);
        });

        /** The reason for the current render (mostly interesting for debugging purposes) */
        self._renderReason = '';

        /** Indicate wheter the first initial render has been run or not */
        self._firstRenderDone = false;

        /** Indicate wheter the first connection to the DOM has been run or not */
        self._firstConnectedDone = false;

        /** Indicate wheter a requested async render is in progress or not */
        self._requestedAsyncRender = false;

        /**
         * Property used in attributeChangedCallback to help seperate attributes beeing set by
         * calling internal $setAttrib method and beeing set externally by element.setAttribute.
         */
        self._newAttribValue = emptyAttrib;

        /**
         * List of currently changed properties/attributes that are batched and triggers an onAttributesChanged event
         * @type {import("./lithtmltypes").PropOrAttribChanged[]}
         */
        self._chPropOrAttrs = [];

        /** Dictionary of all inital attributes on the element (populated in onFirstConnected) */
        self.$attribs = Object.create(null);

        /**
         * Give the derived class the possibility to do some initalizing before setting up observed properties.
         * This can be useful to set the default value of observed properties without them triggering an
         * onPropertyChanged event.
         */
        self.initalize?.();

        // Make sure we know when the observed properties are changed so that we can batch trigger a rerender
        // and trigger an onPropertyChanged event
        self._obsProps.forEach(propName => self._addReactiveProperty(propName));
    }

    /**
     * Defines a get/set wrapper around a property on the instance
     * @param {string} propName the property name
     */
    _addReactiveProperty(propName) {
        let self        = this;
        let changedProp = 'onChanged_' + propName;

        defineProperty(self, propName, self[propName], (propName, oldValue, value) => {
            self._chPropOrAttrs.push({ name: propName, oldValue: oldValue, newValue: value });
            self._createRenderTask('observedPropChange');
            self.onPropertyChanged?.(propName, oldValue, value);
            self[changedProp]?.(oldValue, value);

            let attrName = self._propToAttrs[propName];

            if (attrName && !self._attribChangeInProgress) {
                self._propChangeInProgress = true;
                self.$setAttrib(attrName, value);
                self.onAttributeChanged?.(attrName, oldValue, value);
                self._propChangeInProgress = false;
            }
        });
    }

    /**
     * Create a task for rendering. The render is batched, meaning that updating the same property multiple
     * times or updating multiple different properties or attributes will only result in one render beeing called
     * at the end of the code running.
     * @param {string} renderReason description of the reason for calling this method
     */
    _createRenderTask (renderReason) {
        let self = this;

        if (self._renderTask) {
            return self._renderTask;
        }

        this._renderReason = this._renderReason || renderReason;

        // Create a (micro)task for batch calling rendering
        self._renderTask = true;
        queueMicrotask(() => {
            // Check to see if a _renderTask is still active. If ex. a render has just been done, the _renderTask is null
            // and there is no reason to do another render.
            self._renderTask && self._render();
        });

        return this;
    }

    /**
     * Calls the inherited render method and renders the result to DOM. It triggers the
     * shouldRender, willRender and onRendered methods, and the render can be stopped by
     * returning false from the shouldRender method (unless force is specified).
     * @param {boolean} [force] whether the render should be done no matter what
     */
    _render(force) {
        let self = this;

        if (self._destroyed) {
            return;
        }

        // Do not render anything before the inital connection to the DOM has run. The reason we're preventing
        // this is that the shadowRoot (if in use) is not created before the initial connectedCallback has run.
        if (!self._firstConnectedDone) {
            self._renderTask = null;
            return;
        }

        let changedPropOrAttribs = self._chPropOrAttrs.splice(0);

        // Allow the component to stop the rendering
        let shouldRender = !force && self.shouldRender && self.shouldRender(changedPropOrAttribs);

        if (!force && shouldRender === false) {
            self._renderTask = null;
            return;
        }

        // Give the component a last change to update properties used in the render
        self.willRender?.(changedPropOrAttribs);

        // Trigger the onFirstRender if not already done
        if (!self._firstRenderDone && self.isConnected) {
            self.onFirstRender?.();
            self.$triggerEvent(self._tag + '-firstrender');
        }

        self._renderTask = null;
        self._chPropOrAttrs.length = 0;

        render(self.render(self._renderReason), self.shadowRoot || self, { host: self });

        // Trigger the onFirstRendered if not already done
        if (!self._firstRenderDone && self.isConnected) {
            self._firstRenderDone = true;
            self.onFirstRendered?.();
            self.$triggerEvent(self._tag + '-firstrendered');
        }
        self._renderReason = null;

        // Important that code in onRendered does not trigger a rerender, it's supposed to be
        // used to ex. set focus to an element etc. But in case it happens we try here to convert
        // it to an async render.
        if (self.onRendered) {
            self.onRendered(changedPropOrAttribs);

            if (self._renderTask) {
                self._renderTask = null;
                self.requestAsyncRender('renderRequestInOnRendered')
            }
        }
    }

    /**
     * Trigger and/or dispatch an event
     * @param {string} eventName the name of the event
     * @param {boolean} [dispatch] dispatch an event (default true)
     * @param {boolean} [event] trigger an lithtml event (default true)
     */
    $triggerEvent(eventName, { dispatch = true, event = true, data } = {}) {
        let self = this;

        dispatch && self.dispatchEvent(new CustomEvent(eventName, { bubbles: true, detail: data }));
        event    && events.emit(eventName, () => ({ event: eventName, target: self, id: self.id, data }));

        return self;
    }

// TODO: https://stackblitz.com/edit/lit-html-script-tag?file=src%2Fscript.js

    /**
     * Force an immediate render, use if a synchronous render is needed.
     */
    forceRender(renderReason) {
        this._renderReason = renderReason || 'forced';
        this._render(true);

        return this;
    }

    /**
     * Request a microtask-async render, use if some data not in observedAttributes or observedProperties
     * is updated and an update of the DOM is needed.
     * @param {string} [renderReason] info about the reason for rendering (mostly useful for debugging)
     */
    requestRender(renderReason) {
        return this._createRenderTask(renderReason || 'requested');
    }

    /**
     * Request an async render, use if a 'real' async is needed, and not a microtask async render.
     * @param {string} [renderReason] info about the reason for rendering (mostly useful for debugging)
     */
    requestAsyncRender(renderReason) {
        let self = this;

        if (self._requestedAsyncRender) {
            return self;
        }

        self._requestedAsyncRender = true;

        setZeroTimeout(() => {
            self._createRenderTask(renderReason || 'asyncRequested');
            self._requestedAsyncRender = false;
        });

        return self;
    }

    /** Event called when the CustomElement is removed from the DOM */
    disconnectedCallback() {
        let self   = this;
        let parent = self;

        // If the element still have a parent here, it's probably because the element is beeing moved
        // around the DOM and we'll try to trigger an onMoved event instead of an onDisconnect event
        while (parent.parentElement) { parent = parent.parentElement; }

        if (parent === document.documentElement) {
            self._justDisconnected = true;

            queueMicrotask(() => {
                // If still self._justDisconnected that means that connected has not been run,
                // which again means that the this time the element has not been moved but been
                // disconnected for real
                if (self._justDisconnected) {
                    self._justDisconnected = false;
                    self.onDisconnected?.();
                    self.$triggerEvent(self._tag + '-disconnected');
                }
            });

            return;
        };

        self.onDisconnected?.();
        self.$triggerEvent(self._tag + '-disconnected');
    }

    /** Event called when the CustomElement is added/attached to the DOM */
    connectedCallback() {
        let self = this;

        // If self._justDisconnected is true this means that the element has been moved instead
        // of having a 'real' disconnect, so we make sure that we trigger a moved event instead
        // of connected.
        if (self._justDisconnected) {
            self._justDisconnected = false;
            self.onMoved?.();
            self.$triggerEvent(self._tag + '-moved');
            return;
        }

        if (!self._firstConnectedDone) {
            self._firstConnectedDone = true;

            // Populate all initial attributes
            self.getAttributeNames().forEach(attrName => {
                let propName = camelize(attrName);

                self.$attribs[propName] = self[propName] || self.$getValFromString(self.getAttribute(attrName));
            })

            // Create shadowRoot? The default is true, but it can be overridden by using the attribute use-shadow-root.
            //let useShadowRoot = self.$getAttrib('use-shadow-root');
            //if (useShadowRoot === true || useShadowRoot === false) {
            //    self.options.useShadowRoot = useShadowRoot;
            //}

            self.options.useShadowRoot && self.attachShadow({ mode: 'open' });

            // If the user is has tried to set the shadowStyle before the shadowRoot is created
            // we now make sure it's set
            if (self._css) {
                self.$setShadowStyle(self._css);
                self._css = null;
            }

            self.onFirstConnected?.();
            self.$triggerEvent(self._tag + '-firstconnected');

            // Make sure we trigger an initial render. After the render has run once, there is no need
            // to trigger a new one each time the element is reconnected, since any changes demanding a
            // render will happen based on prop/attrib changes or specifically calling for it.
            self._createRenderTask('onFirstConnected');
        }

        self.onConnected?.();
        self.$triggerEvent(self._tag + '-connected');
    }

    /** Event called when the CustomElement is adopted (moved from one DOM to another) */
    adoptedCallback() {
        let self = this;

        self.$triggerEvent(self._tag + '-adopted');
        self.onAdopted?.();
    }

    /**
     * Event called when an observed attribute changes value
     * @param {string} name the name of the attribute
     * @param {string} oldValue the old value of the attribute
     * @param {string} newValue the new value of the attribute
     */
    attributeChangedCallback(name, oldValue, newValue) {
        let self = this;

        if ((oldValue === newValue) || self._propChangeInProgress) {
            return;
        }
        // We have a backing field for all observed attributes
        let propName      = self._attrToProps[name];
        let propFieldName = '$$prop$$' + (propName || '').toLowerCase();

        // Get the correct old value
        if (propFieldName in self) {
            oldValue = self[propFieldName];
        }

        // Get the 'correct' newValue based on it coming from .setAttribute or $setAttrib
        newValue = self._newAttribValue !== emptyAttrib ? self._newAttribValue : self.$getValFromString(newValue);

        if (oldValue === newValue) {
            return;
        }

        self._createRenderTask('observedAttribChange');
        self.onAttributeChanged?.(name, oldValue, newValue);

        // Setting the property here will trigger an onPropertyChanged event which will
        // also make sure the _chPropOrAttrs array gets updated
        self._attribChangeInProgress = true

        propName && (self[propName] = newValue);
        self._attribChangeInProgress = false
    }

    /**
     * The render method, called each time the custom element needs to be updated/rendered
     * @param {string} renderReason the reason for the render beeing called
     * @returns {import("./types/lit-html").TemplateResult} the template to be rendered
     * */
    render(renderReason) {
        return nothing;
    }

    /** Destroys the element */
    destroy() {
        let self = this;

        if (self._destroyed) {
            return;
        }

        self._destroyed = true;

        self.onDestroy?.();

        self._newAttribValue = null;
        self.options         = null;

        self._obsAttrs.length = 0;
        self._obsAttrs        = null;

        self._chPropOrAttrs.length = 0;
        self._chPropOrAttrs        = null;

        self._obsProps.forEach(prop => {
            delete self['$$prop$$' + prop.toLowerCase()];
            delete self[prop];
        });
        self._obsProps.length = 0;
        self._obsProps        = null;

        self._attrToProps = null;
        self._propToAttrs = null;
        self._css         = null;
        self.$attribs     = null;

        self.parentElement?.removeChild(self);

        self.render = () => nothing;

        self.$triggerEvent(self._tag + '-destroyed', { dispatch: false });
    }

    /**
     * Appends an eventlistener (this is a wrapper around addEventListener)
     * @param {string} eventName the event to react to
     * @param {EventListenerOrEventListenerObject} callback the event handler
     * @param {boolean|AddEventListenerOptions|undefined} [options] options
     * @returns {CustomHTMLElement}
     */
    on(eventName, callback, options) {
        this.addEventListener(eventName, callback, options);
        return this;
    }

    /**
     * Removes an eventlistener (this is a wrapper around removeEventListener)
     * @param {string} eventName
     * @param {EventListenerOrEventListenerObject} callback
     * @param {boolean|AddEventListenerOptions|undefined} options
     * @returns {CustomHTMLElement}
     */
    off(eventName, callback, options) {
        this.removeEventListener(eventName, callback, options);
        return this;
    }

    /**
     * Gets the value of an attribute. The value returned is the string converted value, which can be string, number, boolean, object, array, ...
     * @param {string} attr the attribute name
     * @returns {any} the attribute value
     */
    $getAttrib(attr) {
        let self = this;

        // If the attribute is also a property where we use get/set then make sure we return
        // the 'real' value of the attribute, and not just the string representation (attributes are always strings).
        let propName = camelize(attr);

        if (self._obsProps.indexOf(propName) !== -1){
            return self[propName];
        }

//        if (attr in self) {
//window.console.log('$getAttrib', attr, '- in self')
//            return self[attr];
//        }

        return self.$getValFromString(self.getAttribute(attr));
    }

    /**
     * Sets an attribute value
     * @param {string} attr the attribute name
     * @param {any} val the value of the attribute
     */
    $setAttrib(attr, val) {
        let self     = this;
        let propName = '$$prop$$' + (self._attrToProps[attr] || '').toLowerCase();

        // If the attribute is also an observed attribute/property then make sure we keep
        // the 'real' value of the attribute, and not just the string representation (attributes are always strings).
        self._newAttribValue = propName in self ? val : emptyAttrib;

        // NB! Calling set/remove-Attribute here triggers an attributeChangedCallback for
        //     observed attributes, which is the reason we set the _newAttribValue so that the
        //     callback can differentiate between internal set attributes and external.
        if (!val && val !== false) {
            self.removeAttribute(attr);
        }
        else {
            (isObject(val) || isArray(val))
                ? self.setAttribute(attr, JSON.stringify(val))
                : self.setAttribute(attr, val);
        }

        self._newAttribValue = emptyAttrib;

        return self;
    }

    /**
     * Tries to convert a string to it's correct type
     * @param {string} val the string to be converted
     */
    $getValFromString(val) {
        if (!val) {
            return val;
        }

        if (val[0] === '{') {
            if (val !== '{}' && val.indexOf(':') === -1) {
                return val;
            }

            return tryParse(val) || val;
        }

        if (val[0] === '[') {
            return tryParse(val) || val;
        }

        let number = parseFloat(val);

        if (!isNaN(number)) {
            return number;
        }

        return val === 'true'
            ? true
            : val === 'false'
                ? false
                : val === 'null'
                    ? null
                    : val === 'undefined'
                        ? undefined
                        : val;
    }

    /**
     * Sets the styling of the element
     * @param {string} css the css
     */
    $setShadowStyle(css) {
        let self = this;

        // Need to make sure that the shadowRoot exists, which has happened when firstConnected is called
        if (!self._firstConnectedDone) {
            self._css = css;
            return self;
        }

        if (!self.shadowRoot) {
            throw {
                message: 'Can only use $setShadowStyle on web component using shadowRoot'
            }
        }

        css = css.replace('<style>', '').replace('</style>', '');

        // Use adoptedStyleSheets?
        //  INFO:
        //      https://github.com/lit/lit/blob/00b8c56422fc2577e265393cec6726a4e71a561f/packages/reactive-element/src/css-tag.ts#L99
        //      https://developer.mozilla.org/en-US/docs/Web/API/Document/adoptedStyleSheets
        //      https://web.dev/constructable-stylesheets/
        if ('adoptedStyleSheets' in Document.prototype && 'replace' in CSSStyleSheet.prototype) {
            let sheet = new CSSStyleSheet();

            sheet.replaceSync(css);
            self.shadowRoot.adoptedStyleSheets = [sheet];
        }
        else {
            let style = document.createElement('style');

            style.textContent = css;
            self.shadowRoot.appendChild(style);
        }

        return self;
    }
}

class CustomTokenHTMLElement extends CustomHTMLElement {
    /**
     * Constructor
     */
    constructor(DerivedClass, options) {
        super(DerivedClass, options);

        let self = this;

        self._addReactiveProperty('userData');
        self._checkForUserInfoAndToken = self._checkForUserInfoAndToken.bind(self);
    }

    /**
     * Callback called when the custom element has been connected to the DOM for the first time
     * NB! Inherited Custom Elements *must* call super.onFirstConencted() if overriding this method
     */
    onFirstConnected() {
        this._checkForUserInfoAndToken();
    }

    /**
     * Called when destroy has been called on the element, so we do some cleanup
     * NB! Inherited Custom Elements *must* call super.onDestroy() if overriding this method
     */
    onDestroy () {
        let self = this;

        self._timeoutTokenId && clearTimeout(self._timeoutTokenId);
        self._timeoutTokenId = null;
    }

    /**
     * Make sure that we do not stop asking for userinfo and token until we get them. We do this
     * in case the user attaches an eventlistener for the 'needUserInfoAndToken' event after the
     * html element is attached to the DOM. The events bubble, so this can easily be avoided by
     * ex. attaching the eventlistener on the document (which will then automatically also work
     * for dynamically added elements)
     */
    _checkForUserInfoAndToken() {
        let self = this;

        if (self.userData) {
            return;
        }

        if (self._checkedNoOfTimes > 10) {
            return;
        }


        self._checkedNoOfTimes = self._checkedNoOfTimes || 0;
        self._checkedNoOfTimes++;
        self.$triggerEvent('web-component-needUserInfoAndToken')

        setTimeout(self._checkForUserInfoAndToken, 25);
    }

    /**
     * Updated the userinfo and token
     */
    updateUserInfoAndToken(tokenAndUserInfo) {
        if (!tokenAndUserInfo.accessToken) {
            window['console'].log('Missing accesstoken');
            return;
        }

        let self  = this;
        let token = JSON.parse(window.atob(tokenAndUserInfo.accessToken.split('.')[1]));

        tokenAndUserInfo.identity = tokenAndUserInfo.userInfo;

        self.userData = tokenAndUserInfo;
        self.onUserData?.(self.userData);

        // Make sure we request a new updated token token when the current one is about to expire
        let now          = parseInt(Date.now() / 1000);
        let timeoutInSec = (token.exp - now - 10);

        //
        if (timeoutInSec <= 0) {
            timeoutInSec = 20;
        }

        self._timeoutTokenId && clearTimeout(self._timeoutTokenId);
        self._timeoutTokenId = setTimeout(() => self.$triggerEvent('web-component-needUserInfoAndToken'), timeoutInSec * 1000);
    }
}



/**
 * Parses a string template and converts it to a TemplateResult calling function, of which the
 * result can be used in the render function
 * @type {typeof import('./lithtmltypes').CreateTemplateFn}
 */
let createTemplateFn = (strTemplate, options) => {
    let { context, contextParams, thisContext } = options || {};

    let templateFn = new Function('lithtml', '$context', `
        let { ${ (contextParams || []).join(',') } } = $context;
        let { nothing,noChange,html,svg,repeat,cache,choose,classMap,guard,ifDefined,join,keyed,live,map,range,ref,createRef,styleMap,until,when,asyncAppend,asyncReplace,templateContent,unsafeHTML,unsafeSVG,createTemplateFn,utils,virtualize,virtualizerRef } = lithtml;
        return html${ ('`' + strTemplate + '`') };`);

    if (thisContext) {
        templateFn = templateFn.bind(thisContext);
    }

    return (ctx) => templateFn(lithtml, ctx || context || {});
}

/**
 * Takes a template string and optional context/data and renders the template to a DocumentFragment
 * @type {typeof import('./lithtmltypes').renderStrToEl}
 */
let renderStrToEl = (strTemplate, options) => {
    let htmlFn = createTemplateFn(strTemplate, options);
    let target = new DocumentFragment();

    render(htmlFn(), target);

    return target;
}

/**
 * Takes a template string and optional context/data and renders the template to a string
 * @type {typeof import('./lithtmltypes').RenderStrToHtmlFn}
 */
let renderStrToHtml = (strTemplate, options) => {
    let fragment = renderStrToEl(strTemplate, options);
    let tempEl   = document.createElement('script');

    tempEl.type='text/html';
    tempEl.appendChild(fragment)

    return tempEl.innerHTML.trim().replaceAll(/\x3C![^>]*>/gi, '');
}

/**
 * Add translations recursively to the root of the translation object
 * @param {string} prefix the prefix added in fron of the translation keys (ex. '', 'Status.', ...)
 * @param {object} langObj the object containing the language translations
 * @param {object} translationObj the resulting translation object
 * @param {number} [index] optional index into the array of translations
 */
let addLanguageRec = (prefix, langObj, translationObj, index = -1) => {
    Object.keys(langObj).forEach(key => {
        let text = langObj[key];

        if (isObject(text)) {
            addLanguageRec(prefix + key + '.', text, translationObj, index);
        }
        else {
            translationObj[prefix + key] = index === -1 ? text : text[index];
        }
    });
}

/**
 * Initializes a new translations function
 * @param tagOrBaseTranslationUrl [string] the tag or the base url for language json files that may be downloaded
 * @param version [string=] the tag version
 */
let translateInit = (tagOrBaseTranslationUrl, version) => {
    let translations    = Object.create(null)
    let currTranslation = defTranslation = translations
    let translationBaseUrl

    if (version) {
        translationBaseUrl = `https://cdnpowel.azureedge.net/scripts/webcomponents/${tagOrBaseTranslationUrl}/${version}`;
    }
    else if (tagOrBaseTranslationUrl) {
        translationBaseUrl = tagOrBaseTranslationUrl
    }

    /** @type{import('./lithtmltypes').TranslateFunc} */
    let t = (key) => currTranslation[key] || defTranslation[key] || key;

    t.add = (lang, data) => {
        let langName;

        if (typeof(lang) === 'string') {
            langName = lang;
            addLanguageRec('', data, translations[lang] || (translations[lang] = Object.create(null)));
        }
        else if (isArray(lang)) {
            langName = lang[0];
            lang.forEach((langName, index) => addLanguageRec('', data, translations[langName] || (translations[langName] = Object.create(null)), index));
        }
        else if (isObject(lang)) {
            langName = Object.keys(lang)[0];
            Object.keys(lang).forEach(langName => addLanguageRec('', lang[langName], (translations[langName] = Object.create(null))))
        }

        // Set default language if not already done
        let globalLang = translations[lithtml.translation.language];

        if (globalLang) {
            if (currTranslation === translations) {
                currTranslation = globalLang;
            }

            if (defTranslation === translations) {
                defTranslation = globalLang;
            }
        }

        // Make sure we do have a translation in case the global translation is missing
        if (currTranslation === translations) {
            currTranslation = defTranslation = translations[langName];
        }

        return t;
    }

    t.defTranslation = (langName) => {
        let translationToUse = translations[langName];

        if (translationToUse) {
            defTranslation = translationToUse;
        }

        return t;
    }

    t.useLang = (langName) => {
        let translationToUse = translations[langName];

        if (translationToUse) {
            currTranslation = translationToUse;

            if (defTranslation === translations) {
                defTranslation = currTranslation;
            }
        }

        return t;
    }

    t.load = (langName) => {
        if (translations[langName]) {
            t.useLang(langName);

            return Promise.resolve();
        }

        if (!translationBaseUrl) {
            return Promise.resolve();
        }

        return fetch(`${translationBaseUrl}/${langName}.json`)
            .then(response => {
                if (response.ok) {
                    return response.json();
                }
            })
            .then(data => {
                if (data) {
                    t.add(langName, data).useLang(langName);
// TODO: When done, call t.onLangLoaded()
                }

                return data;
            });
    }

    t.setTranslation = (langObj) => {
        currTranslation = langObj;

        return t;
    }

    t.destroy = () => {
        translations    = Object.create(null);
        currTranslation = null;
        defTranslation  = null;
    }

    return t;
}

/** The global lithtml object */
let lithtml = Object.create(events);

/** The current language */
let currLanguage = (location.href.split('lang=')[1] || '').substring(0,2);

if (!currLanguage) {
    currLanguage = window.appContext?.language;
}

if (!currLanguage) {
    try {
        currLanguage = localStorage.getItem('lang');
    }
    catch(e){}
}

if (!currLanguage) {
    currLanguage = 'en';
}

lithtml.render   = render  ;
lithtml.nothing  = nothing ;
lithtml.noChange = noChange;
lithtml.html     = html    ;
lithtml.svg      = svg     ;

lithtml.repeat          = repeat         ;
lithtml.cache           = cache          ;
lithtml.choose          = choose         ;
lithtml.classMap        = classMap       ;
lithtml.guard           = guard          ;
lithtml.ifDefined       = ifDefined      ;
lithtml.join            = join           ;
lithtml.keyed           = keyed          ;
lithtml.live            = live           ;
lithtml.map             = map            ;
lithtml.range           = range          ;
lithtml.ref             = ref            ;
lithtml.createRef       = createRef      ;
lithtml.styleMap        = styleMap       ;
lithtml.until           = until          ;
lithtml.when            = when           ;
lithtml.asyncAppend     = asyncAppend    ;
lithtml.asyncReplace    = asyncReplace   ;
lithtml.templateContent = templateContent;
lithtml.unsafeHTML      = unsafeHTML     ;
lithtml.unsafeSVG       = unsafeSVG      ;
//lithtml.virtualize      = virtualize     ;
//lithtml.virtualizerRef  = virtualizerRef ;

lithtml.CustomHTMLElement      = CustomHTMLElement;
lithtml.CustomTokenHTMLElement = CustomTokenHTMLElement;

lithtml.utils = utils;

/**
 * preact signals
 * @type {import('./lithtmltypes').Signals}
 */
lithtml.signals = {
    signal,
    batch,
    computed,
    effect,
    subscribe: (signalOrSignals, cb, { runOnSubscribe, batchCallback } = {}) => {
        let signals  = isArray(signalOrSignals) ? signalOrSignals : [signalOrSignals];
        let firstRun = true;
        let batching = false;

        return effect(() => {
            signals.forEach(signal => signal.value);

            // Callback immediately after calling subscribe?
            if (firstRun) {
                firstRun = false;

                if (!runOnSubscribe) {
                    return;
                }
            }

            // If no batching, run callback immediately on every signal change
            if (!batchCallback) {
                cb(signals);
                return;
            }

            if (batching) {
                return;
            }
// TODO: call dispose here? and subscribe again in the resolved promise? maybe a problem with code that keeps track of the dispose function... --> could be solved with returning an object with dispose in? no need for 'batching' variable.
            batching = true;

            queueMicrotask(() => {
                cb(signals);
                batching = false;
            });
        });
    }
}

/**
 * @type {import('./lithtmltypes').Translation}
 */
lithtml.translation = {
    get language () { return currLanguage; },
    init   : translateInit,
    useLang: (langName) => {
        if (langName && (currLanguage !== langName)) {
            currLanguage = langName;

            lithtml.emit('language-changed', { lang: currLanguage });
        }
    }
};

let stateObj = eventify(Object.create(null));

delete stateObj.deEventify;
delete stateObj.clearEvents;

defineReadOnlyProperty(lithtml, 'state', Object.create(stateObj));

lithtml.createTemplateFn = createTemplateFn;
lithtml.renderStrToHtml  = renderStrToHtml ;
lithtml.renderStrToEl    = renderStrToEl   ;
lithtml.eventify         = eventify        ;
lithtml.version          = version         ;

if (!window.lithtml || (window.lithtml.version < lithtml.version)) {
    window.lithtml = lithtml;
}

// Copy this for module min build
//export const { render, nothing, noChange, html, svg, repeat, cache, choose, classMap, guard, ifDefined, join, keyed, live, map, range, ref, createRef, styleMap, until, when, asyncAppend, asyncReplace, templateContent, unsafeHTML, unsafeSVG, CustomHTMLElement, createTemplateFn, eventify, version, emitOnce, emitAsync, emit, once, off, on } = lithtml;

// Use this for buildtest
//export { render, nothing, noChange, html, svg, repeat, cache, choose, classMap, guard, ifDefined, join, keyed, live, map, range, ref, createRef, styleMap, until, when, asyncAppend, asyncReplace, templateContent, unsafeHTML, unsafeSVG, CustomHTMLElement };

/*
    Example of generating dynamic html with lithtml

        let a = document.createElement('div');
        let b = lithtml.createTemplateFn('<b>test ${this.location.href}</b>', { thisContext: window });
        lithtml.render(b(), a);
        a.innerHTML;


        lithtml.render(lithtml.html`<b>testcccccc</b>`, document.body)



*/